import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { 
  Search, MapPin, Clock, AlertTriangle, Users, Eye, 
  Filter, ChevronLeft, Grid, List, SlidersHorizontal, X, Lock
} from 'lucide-react';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  
  const [filters, setFilters] = useState({
    search: searchParams.get('q') || '',
    status: '',
    city: '',
    state: '',
    is_urgent: false,
    is_child: false,
    is_elderly: false,
    min_age: '',
    max_age: '',
    sort_by: 'created_at',
    order: 'desc'
  });

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async (filterOverrides = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const currentFilters = { ...filters, ...filterOverrides };
      
      if (currentFilters.search) params.append('search', currentFilters.search);
      if (currentFilters.status) params.append('status', currentFilters.status);
      if (currentFilters.city) params.append('city', currentFilters.city);
      if (currentFilters.state) params.append('state', currentFilters.state);
      if (currentFilters.is_urgent) params.append('is_urgent', 'true');
      if (currentFilters.is_child) params.append('is_child', 'true');
      if (currentFilters.is_elderly) params.append('is_elderly', 'true');
      if (currentFilters.min_age) params.append('min_age', currentFilters.min_age);
      if (currentFilters.max_age) params.append('max_age', currentFilters.max_age);
      params.append('sort_by', currentFilters.sort_by);
      params.append('order', currentFilters.order);
      params.append('limit', '50');

      const response = await axios.get(`${API}/cases?${params.toString()}`);
      setCases(response.data);
    } catch (error) {
      console.error('Failed to fetch cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCases();
    setSearchParams(filters.search ? { q: filters.search } : {});
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  };

  const applyFilters = () => {
    fetchCases();
    setShowFilters(false);
  };

  const clearFilters = () => {
    const cleared = {
      search: '',
      status: '',
      city: '',
      state: '',
      is_urgent: false,
      is_child: false,
      is_elderly: false,
      min_age: '',
      max_age: '',
      sort_by: 'created_at',
      order: 'desc'
    };
    setFilters(cleared);
    fetchCases(cleared);
    setSearchParams({});
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric' 
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'MISSING', class: 'bg-red-600 text-white border-0' },
      under_investigation: { label: 'Investigating', class: 'bg-amber-500 text-white border-0' },
      sighting_confirmed: { label: 'Sighting Confirmed', class: 'bg-blue-600 text-white border-0' },
      located_safe: { label: 'FOUND', class: 'bg-green-600 text-white border-0' },
      closed: { label: 'Closed', class: 'bg-slate-500 text-white border-0' }
    };
    const config = statusConfig[status] || statusConfig.active;
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <img src={LOGO_URL} alt="MISYOU" className="w-10 h-10 rounded-xl object-cover" />
              <span className="text-xl font-black text-slate-900 hidden sm:block">MISYOU</span>
            </Link>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search by name, location..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10 h-11 bg-white"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" className="h-11 px-6" data-testid="search-btn">
                Search
              </Button>
            </form>

            <Button 
              variant="outline" 
              className="shrink-0"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="filter-toggle"
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters Panel */}
        {showFilters && (
          <Card className="mb-6 bg-white shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Filter Cases</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm mb-2 block">Status</Label>
                  <Select value={filters.status || "all"} onValueChange={(v) => handleFilterChange('status', v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="filter-status">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="under_investigation">Under Investigation</SelectItem>
                      <SelectItem value="sighting_confirmed">Sighting Confirmed</SelectItem>
                      <SelectItem value="located_safe">Found Safe</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm mb-2 block">City</Label>
                  <Input
                    placeholder="Any city"
                    value={filters.city}
                    onChange={(e) => handleFilterChange('city', e.target.value)}
                    data-testid="filter-city"
                  />
                </div>

                <div>
                  <Label className="text-sm mb-2 block">State</Label>
                  <Input
                    placeholder="Any state"
                    value={filters.state}
                    onChange={(e) => handleFilterChange('state', e.target.value)}
                    data-testid="filter-state"
                  />
                </div>

                <div>
                  <Label className="text-sm mb-2 block">Sort By</Label>
                  <Select value={filters.sort_by} onValueChange={(v) => handleFilterChange('sort_by', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="created_at">Date Reported</SelectItem>
                      <SelectItem value="last_seen_date">Last Seen Date</SelectItem>
                      <SelectItem value="full_name">Name</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="urgent"
                    checked={filters.is_urgent}
                    onCheckedChange={(v) => handleFilterChange('is_urgent', v)}
                  />
                  <Label htmlFor="urgent" className="text-sm cursor-pointer">Urgent Only</Label>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="child"
                    checked={filters.is_child}
                    onCheckedChange={(v) => handleFilterChange('is_child', v)}
                  />
                  <Label htmlFor="child" className="text-sm cursor-pointer">Children Only</Label>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="elderly"
                    checked={filters.is_elderly}
                    onCheckedChange={(v) => handleFilterChange('is_elderly', v)}
                  />
                  <Label htmlFor="elderly" className="text-sm cursor-pointer">Elderly Only</Label>
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <Button onClick={applyFilters} className="rounded-full" data-testid="apply-filters">
                  Apply Filters
                </Button>
                <Button variant="outline" onClick={clearFilters} className="rounded-full">
                  Clear All
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {filters.search ? `Results for "${filters.search}"` : 'All Cases'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {cases.length} case{cases.length !== 1 ? 's' : ''} found
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="icon"
              onClick={() => setViewMode('grid')}
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="icon"
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="py-20 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto"></div>
            <p className="text-muted-foreground mt-4">Searching...</p>
          </div>
        ) : cases.length === 0 ? (
          <div className="py-20 text-center">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No cases found</h3>
            <p className="text-muted-foreground">Try adjusting your search or filters</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {cases.map((caseItem) => (
              <Link to={`/case/${caseItem.case_id}`} key={caseItem.case_id}>
                <Card 
                  className="overflow-hidden card-hover bg-white border-slate-100 shadow-lg"
                  data-testid={`search-case-${caseItem.case_id}`}
                >
                  <div className="relative">
                    {caseItem.photos && caseItem.photos.length > 0 ? (
                      <>
                        <img 
                          src={caseItem.photos[0]} 
                          alt={caseItem.full_name}
                          className={`w-full h-56 object-cover ${caseItem.photos_visible === false ? 'blur-xl' : ''}`}
                        />
                        {caseItem.photos_visible === false && (
                          <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white">
                            <Lock className="w-8 h-8 mb-2" />
                            <span className="text-sm font-medium">Subscription Expired</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-56 bg-slate-200 flex items-center justify-center">
                        <Users className="w-12 h-12 text-slate-400" />
                      </div>
                    )}
                    <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                      {getStatusBadge(caseItem.status)}
                      {caseItem.is_urgent && (
                        <Badge className="bg-amber-500 text-white border-0">URGENT</Badge>
                      )}
                      {caseItem.is_child && (
                        <Badge className="bg-purple-600 text-white border-0">CHILD</Badge>
                      )}
                      {caseItem.priority_boosted && (
                        <Badge
                          className="bg-slate-900 text-white border-0"
                          data-testid={`priority-badge-${caseItem.case_id}`}
                        >
                          PRIORITY
                        </Badge>
                      )}
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-lg text-slate-900 mb-2">{caseItem.full_name}</h3>
                    <div className="space-y-1.5 text-sm text-muted-foreground">
                      {caseItem.age && <p>Age: {caseItem.age} years</p>}
                      {caseItem.last_seen_location?.city && (
                        <p className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {caseItem.last_seen_location.city}, {caseItem.last_seen_location.state}
                        </p>
                      )}
                      {caseItem.last_seen_date && (
                        <p className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {formatDate(caseItem.last_seen_date)}
                        </p>
                      )}
                    </div>
                    <Button className="w-full mt-4 rounded-full btn-press">
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {cases.map((caseItem) => (
              <Link to={`/case/${caseItem.case_id}`} key={caseItem.case_id}>
                <Card 
                  className="overflow-hidden hover:shadow-lg transition-shadow bg-white"
                  data-testid={`search-case-${caseItem.case_id}`}
                >
                  <CardContent className="p-4 flex gap-4">
                    <div className="w-24 h-24 rounded-lg overflow-hidden bg-slate-200 shrink-0 relative">
                      {caseItem.photos && caseItem.photos.length > 0 ? (
                        <>
                          <img 
                            src={caseItem.photos[0]} 
                            alt={caseItem.full_name}
                            className={`w-full h-full object-cover ${caseItem.photos_visible === false ? 'blur-xl' : ''}`}
                          />
                          {caseItem.photos_visible === false && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <Lock className="w-6 h-6 text-white" />
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Users className="w-8 h-8 text-slate-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-bold text-lg text-slate-900">{caseItem.full_name}</h3>
                        {getStatusBadge(caseItem.status)}
                        {caseItem.is_urgent && (
                          <Badge className="bg-amber-500 text-white border-0">URGENT</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        {caseItem.age && <span>Age: {caseItem.age}</span>}
                        {caseItem.gender && <span>{caseItem.gender}</span>}
                        {caseItem.last_seen_location?.city && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {caseItem.last_seen_location.city}, {caseItem.last_seen_location.state}
                          </span>
                        )}
                        {caseItem.last_seen_date && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {formatDate(caseItem.last_seen_date)}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Eye className="w-3.5 h-3.5" />
                          {caseItem.sightings_count} sightings
                        </span>
                      </div>
                    </div>
                    <Button className="shrink-0 rounded-full self-center">
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
